import React from 'react';
import {
	Box,
	Checkbox,
	ListItem,
	ListItemIcon,
	ListItemText,
	TextField,
	Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { VatNumberWithValidation } from 'common/modules/billing';
import { RiCheckboxCircleLine, RiLoader3Line } from 'react-icons/ri';
import { useTranslation } from 'services/localization';

interface Props {
	value: VatNumberWithValidation | undefined;
	onChange: (value: VatNumberWithValidation) => void;
	showExempt?: boolean;
}
const VatNumberInput = (props: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const { showExempt } = props;

	const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const number = e.target.value;

		props.onChange({
			value: number,
			validated: false,
			exempt: false,
		});
	};

	const handleExemptChange = () => {
		props.onChange({
			value: '',
			validated: false,
			exempt: !props.value?.exempt,
		});
	};

	const renderValidationStatus = () => {
		if (props.value?.validated) {
			return (
				<Tooltip
					title={t(
						'account.general.vatNumber.vatNumberValidated',
						'This VAT number has been validated by the Rentle billing team',
					)}
				>
					<div>
						<RiCheckboxCircleLine color={theme.palette.success.main} />
					</div>
				</Tooltip>
			);
		}

		if (!!props.value?.value) {
			return (
				<Tooltip
					title={t(
						'account.general.vatNumber.vatNumberPendingValidation',
						'This VAT number is currently being validated by the Rentle billing team. It will not be used in your invoices until it has been validated.',
					)}
				>
					<div>
						<RiLoader3Line color={theme.palette.warning.main} />
					</div>
				</Tooltip>
			);
		}

		return null;
	};

	return (
		<Box>
			<TextField
				label={t('account.general.vatNumber.label', 'Your VAT number')}
				variant="outlined"
				fullWidth
				value={props.value?.value ?? ''}
				disabled={showExempt && !!props.value?.exempt}
				onChange={handleNumberChange}
				InputProps={{
					endAdornment: renderValidationStatus(),
				}}
			/>
			{showExempt && (
				<>
					<Box mt={2} />
					<ListItem button onClick={handleExemptChange}>
						<ListItemIcon style={{ pointerEvents: 'none' }}>
							<Checkbox color="primary" checked={!!props.value?.exempt} />
						</ListItemIcon>
						<ListItemText
							primary={t(
								'account.general.vatNumber.exemptFromVat',
								'My business is exempt from VAT',
							)}
						/>
					</ListItem>
				</>
			)}
		</Box>
	);
};

export default VatNumberInput;
